import { zodResolver } from "@hookform/resolvers/zod";
import { isEmpty } from "ramda";
import { useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { AddOnTextInput, Button, CheckboxInput, IconButton, Skeleton, Typography } from "@/components/atoms";
import { DataTable } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePrompt } from "@/hooks";
import { useDeleteStateBasedDynamicPricingMutation, useUpdateStateBasedDynamicPricingMutation } from "@/redux/apis/config/trip/tripCost";
import { DynamicBaseInterval, DynamicIntervals, DynamicPricingState } from "@/redux/slices/trip/types";
import {
  addToast,
  distanceUnitNoun,
  formatCurrency,
  getCurrencySymbol,
  kmToUnit,
  ratePerKmToUnit,
  ratePerUnitToKmPreserving,
  unitToKm,
  unitToKmPreserving,
} from "@/utils";
import { StateRatesModals } from "./StateRatesModals";
import { generateEditColumns, getPriceRanges, validateIntervals } from "./helpers";
import { StateRatesFormData, createStateRatesFormSchema } from "./stateRatesFormSchema";
import { StateRatesEditAction, StateRatesEditTableData } from "./types";

interface StateRatesEditPanelProps {
  state: DynamicPricingState;
}

export const StateRatesEditPanel = ({ state }: StateRatesEditPanelProps) => {
  const navigate = useNavigate();
  const { name, baseInterval, intervals, currency, distanceUnit } = state;
  const currencySymbol = getCurrencySymbol(currency) || "";
  const [selectedRange, setSelectedRange] = useState<number | null>(null);
  const [action, setAction] = useState<StateRatesEditAction>(undefined);
  const [isOpenEnded, setIsOpenEnded] = useState(true);

  const [updateStateRates, { isLoading: isLoadingUpdate }] = useUpdateStateBasedDynamicPricingMutation();
  const [deleteStateRates, { isLoading: isLoadingDelete }] = useDeleteStateBasedDynamicPricingMutation();
  const isLoading = isLoadingUpdate || isLoadingDelete;

  const {
    register,
    watch,
    handleSubmit,
    control,
    formState: { isDirty, errors, isSubmitted },
  } = useForm<StateRatesFormData>({
    defaultValues: {
      baseInterval: {
        minimumRange: kmToUnit(baseInterval?.minimumRange || 0, distanceUnit),
        maximumRange: 0,
        cost: baseInterval?.cost || 0,
      },
      intervals: isEmpty(intervals)
        ? [
            {
              minimumRange: 0,
              rate: 0,
            },
          ]
        : intervals.map((interval) => ({
            minimumRange: kmToUnit(interval.minimumRange || 0, distanceUnit),
            rate: ratePerKmToUnit(interval.rate || 0, distanceUnit),
          })),
    },
    resolver: zodResolver(createStateRatesFormSchema(distanceUnit)),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "intervals",
  });
  const values = watch();

  const promptWhen = isDirty && !isSubmitted;
  usePrompt({ when: promptWhen, message: "Changes will not be saved. Do you want to proceed?" });

  const onSubmit = handleSubmit((data) => {
    const isValid = validateIntervals([data.baseInterval.minimumRange, ...data.intervals.map((interval) => interval.minimumRange)]);

    if (isValid) {
      // The API is always kilometre based, so anything the operator entered in their own unit converts back here
      const toStoredRange = (value: number, index: number) => unitToKmPreserving(value, intervals[index]?.minimumRange, distanceUnit);

      updateStateRates({
        state: state.state,
        base_interval: {
          range_from: unitToKmPreserving(data.baseInterval.minimumRange, baseInterval?.minimumRange, distanceUnit),
          range_to: isEmpty(fields)
            ? unitToKm(data.baseInterval.maximumRange, distanceUnit)
            : toStoredRange(data.intervals[0].minimumRange, 0),
          interval_cost: data.baseInterval.cost * 100,
          note: "",
        },
        intervals: isEmpty(fields)
          ? []
          : data.intervals.map((i, index) => ({
              range_from: toStoredRange(i.minimumRange, index),
              rate: ratePerUnitToKmPreserving(i.rate, intervals[index]?.rate, distanceUnit) * 100,
              note: "",
            })),
      })
        .unwrap()
        .then(() => {
          addToast("success", "Successfully Updated State Rates");
          navigate("..");
        })
        .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    }
  });

  const handleResetAndDeleteSettings = () => {
    setAction(undefined);
    deleteStateRates({ state: state.state })
      .unwrap()
      .then(() => {
        addToast("success", "Successfully Reset and Deleted Settings");
        navigate("..");
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const handleAddRange = () => {
    append({
      minimumRange:
        values.intervals.length > 0
          ? Number(values.intervals[values.intervals.length - 1].minimumRange) + 1
          : Number(values.baseInterval.minimumRange) + 1,
      rate: 0,
    });
  };

  const handleDeleteRange = (i: number) => {
    setSelectedRange(i);
    setAction("deleteRange");
  };

  const handleConfirmDeleteRange = () => {
    if (selectedRange != null) {
      remove(selectedRange - 1);
      setAction(undefined);
    }
  };

  const generateEditData = (baseInterval: DynamicBaseInterval, intervals: DynamicIntervals) => {
    const priceRanges = baseInterval ? getPriceRanges({ baseInterval, intervals }) : [];
    const jointIntervals = baseInterval ? [baseInterval, ...intervals] : [];
    const data = jointIntervals.map((_, i) => {
      return {
        rangeIntervals: i > 0 ? `Range ${i}` : "Minimum Trip Cost",
        range:
          i > 0 ? (
            <div className="flex flex-col gap-x-2 gap-y-2 md:flex-row lg:flex-row">
              <div className="flex flex-row items-center gap-x-2">
                <AddOnTextInput
                  endAddOn={distanceUnit}
                  type="number"
                  size="sm"
                  className="w-[110px] [&_input]:text-right"
                  value={Number(intervals[i - 1]?.minimumRange)}
                  disabled
                />
                <Typography variant="small" className="text-neutral-dark-gray">
                  -
                </Typography>
              </div>
              {i === jointIntervals.length - 1 ? (
                <>
                  <AddOnTextInput
                    endAddOn={distanceUnit}
                    size="sm"
                    className="w-[110px] [&_input]:text-right"
                    disabled
                    value={`>${intervals[i - 1].minimumRange}`}
                  />
                  <CheckboxInput
                    size="md"
                    variant="check"
                    label="Set as open ended"
                    checked={isOpenEnded}
                    onChange={(e) => setIsOpenEnded(e.target.checked)}
                    disabled
                  />
                </>
              ) : (
                <AddOnTextInput
                  endAddOn={distanceUnit}
                  type="number"
                  size="sm"
                  className="w-[110px] [&_input]:text-right"
                  hasError={!!errors.intervals?.[i]?.minimumRange}
                  step="any"
                  {...register(`intervals.${i}.minimumRange`)}
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2 md:flex-row lg:flex-row">
              <div className="flex flex-row items-center gap-x-2">
                <AddOnTextInput
                  endAddOn={distanceUnit}
                  type="number"
                  size="sm"
                  className="w-[110px] [&_input]:text-right"
                  value={0}
                  disabled
                />
                <Typography variant="paragraph">-</Typography>
              </div>
              <AddOnTextInput
                endAddOn={distanceUnit}
                type="number"
                size="sm"
                className="w-[110px] [&_input]:text-right"
                hasError={!!errors.intervals?.[0]?.minimumRange}
                {...register(`intervals.${0}.minimumRange`)}
              />
            </div>
          ),
        rate:
          i > 0 ? (
            <AddOnTextInput
              startAddOn={`${currency} ${currencySymbol}`}
              type="number"
              size="sm"
              className="w-[175px]"
              hasError={!!errors.intervals?.[i - 1]?.rate}
              step="any"
              {...register(`intervals.${i - 1}.rate`)}
            />
          ) : (
            <Typography variant="paragraph">-</Typography>
          ),
        price:
          i > 0 ? (
            <Typography variant="paragraph">
              {`${formatCurrency(priceRanges[i].min, 2, currency, { skipDecimalConversion: true })}${
                priceRanges[i].max > 0 ? ` - ${formatCurrency(priceRanges[i].max, 2, currency, { skipDecimalConversion: true })}` : "+"
              }`}
            </Typography>
          ) : (
            <AddOnTextInput
              startAddOn={`${currency} ${currencySymbol}`}
              type="number"
              size="sm"
              className="w-[175px]"
              hasError={!!errors.baseInterval?.cost}
              step="any"
              {...register("baseInterval.cost")}
            />
          ),
        action: i > 1 && (
          <div className="flex items-center justify-center">
            <IconButton variant="custom" iconName="Trash" className="text-danger" iconSize="md" onClick={() => handleDeleteRange(i)} />
          </div>
        ),
      } as StateRatesEditTableData;
    });
    return data;
  };

  const data = useMemo(
    () => generateEditData(values.baseInterval as DynamicBaseInterval, values.intervals as DynamicIntervals),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values]
  );

  const columns = useMemo(
    () => generateEditColumns(distanceUnit),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (isLoading) return <Skeleton />;

  return (
    <form onSubmit={onSubmit} className="flex flex-col">
      <div className="flex flex-col justify-between gap-4 xl:flex-row">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">{name}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">{`Set up ${distanceUnitNoun(
            distanceUnit
          )}-based pricing. Add different rates for different distances. For example, ${currencySymbol}5/${distanceUnit} for trips under 10${distanceUnit} and ${currencySymbol}10/${distanceUnit} for longer trips`}</Typography>
        </div>
        <div className="flex flex-row gap-x-3">
          <Button variant="secondary" size="sm" onClick={() => navigate("..")}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={!isDirty}>
            Save
          </Button>
        </div>
      </div>
      <DataTable columns={columns} data={data} className="mt-4 w-full overflow-x-auto rounded-b-none bg-white [&_table]:table-auto" />
      <div className="flex w-full items-center rounded-b-lg bg-white p-3 ring-1 ring-neutral-mid-gray">
        <Button variant="secondary" size="sm" startIcon="Add" onClick={handleAddRange}>
          Add Range
        </Button>
      </div>
      <div className="mt-6 flex items-center justify-center">
        <Button
          variant="secondary"
          size="sm"
          startIcon="Danger"
          iconVariant="Bold"
          iconClassName="text-warning"
          onClick={() => setAction("resetAndDeleteSettings")}
          disabled={!baseInterval}
        >
          Reset and Delete Settings
        </Button>
      </div>
      <StateRatesModals
        action={action}
        selectedRange={selectedRange}
        onDeleteRange={() => handleConfirmDeleteRange()}
        onResetAndDeleteSettings={() => handleResetAndDeleteSettings()}
        onClose={() => setAction(undefined)}
      />
    </form>
  );
};
